import {
  Box,
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
} from "@mui/material";
import { Maybe, User } from "api";
import { Avatar } from "components/FixedHeader/styled";
import React, { FC, Fragment } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import TextPostRender from "components/post/TextPostRender";
import TimeAgo from "components/TimeAgo";
import { DownVoteIcon, UpVoteIcon, ReplyIcon } from "icons";
import { useAuthContext } from "context/AuthContext";
import Pdf from "../Post/Pdf";
import CommentImage from "./CommentImage";
import Name from "components/Name";

interface CompoundProps {
  Card: FC;
  Header: FC<{ user: Maybe<User> }>;
  Body: FC;
  Footer: FC;
  TextComment: FC<{ text: string }>;
  MediaComment: FC<{ media: any }>;
  Loading: FC;
  ButtonWrapper: FC;
  TimeAgo: FC<{ time: any }>;
  Upvote: FC<IconButtonProps & { upvotes: number; isUpvoted: number }>;
  Downvote: FC<IconButtonProps & { downvotes: number; isDownvoted: number }>;
  Reply: FC<IconButtonProps & { replies: number }>;
  WriteYourComment: FC<ButtonProps>;
}

const Comments: FC & CompoundProps = ({ children }) => {
  return <Container>{children}</Container>;
};

Comments.Card = ({ children }) => <Card>{children}</Card>;
Comments.Header = ({ user }) => (
  <Header>
    <Link
      to={`/profile/${encodeURI(`${user?.username}`)}`}
      className="avatar-link"
    >
      <Avatar src={user?.avatar?.LowResUri} />
    </Link>
    <Box flex={1} marginLeft={1}>
      <p className="header-name">
        <Link
          to={`/profile/${encodeURI(`${user?.username}`)}`}
        ><Name user={user} /></Link>
      </p>
      <p className="header-subname">
        <Link to={`/profile/${encodeURI(`${user?.username}`)}`}>
          @{`${user?.username}`}
        </Link>
      </p>
    </Box>
  </Header>
);

Comments.Body = ({ children }) => <Body>{children}</Body>;
Comments.Footer = ({ children }) => <Footer>{children}</Footer>;
Comments.TextComment = ({ text }) => <TextPostRender text={text} />;
Comments.MediaComment = ({ media }) => {
  const docs = media?.filter((x:any) => x?.type.includes('application/')) || [];
  if (docs.length === 0) return null;
  return (
    <MediaContainer>
      {media.map((item:any, key:any) =>
        <a
          href={item?.uri?.showUri}
          target="_blank"
          rel="noreferrer"
          title="Click to view pdf"
          key={key}
        >
          <Pdf pdfThumbnailUrl={item?.uri?.ThumbnailUrl} />
        </a>
      )}
    </MediaContainer>
  );
};
Comments.Loading = ({ children }) => <Footer>{children}</Footer>;
Comments.ButtonWrapper = ({ children }) => (
  <ButtonWrapper>{children}</ButtonWrapper>
);
Comments.TimeAgo = ({ time }) => <TimeAgo time={time} />;
Comments.Upvote = ({ isUpvoted, upvotes, ...rest }) => (
  <IconButtonWrap>
    <IconButton {...rest}>
      <UpVoteIcon
        style={{
          stroke: isUpvoted === 1 ? "black" : "green",
          strokeWidth: 2,
          fill: isUpvoted === 1 ? "green" : undefined,
        }}
      />
    </IconButton>
    <Count>{upvotes}</Count>
  </IconButtonWrap>
);
Comments.Downvote = ({ isDownvoted, downvotes, ...rest }) => (
  <IconButtonWrap>
    <IconButton {...rest}>
      <DownVoteIcon
        style={{
          stroke: isDownvoted === 1 ? "black" : "red",
          strokeWidth: 2,
          fill: isDownvoted === 1 ? "red" : undefined,
        }}
      />
    </IconButton>
    <Count>{downvotes}</Count>
  </IconButtonWrap>
);
Comments.Reply = ({ replies, ...rest }) => (
  <IconButtonWrap>
    <IconButton {...rest}>
      <ReplyIcon
        style={{
          stroke: "#2196f3",
          strokeWidth: 2,
        }}
      />
    </IconButton>
    <Count>{replies}</Count>
  </IconButtonWrap>
);

Comments.WriteYourComment = ({ ...rest }) => {
  const { user } = useAuthContext();
  return (
    <Card>
      <WriteYourComment>
        <Avatar src={user?.avatar?.LowResUri} />
        <Button variant="outlined" {...rest}>
          Comment here...
        </Button>
      </WriteYourComment>
    </Card>
  );
};
export default Comments;

const MediaContainer = styled.div``;

const Container = styled.div`
  /* border-top: 1px solid rgba(0, 0, 0, 0.05); */
  padding: 15px 5px 5px 10px;
`;

const Card = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.065);
  border-radius: 6px;
  background-color: #f9f9f9;
  margin: 0 0 10px 0;
  padding: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px;

  .MuiAvatar-root {
    width: 32px;
    height: 32px;
  }

  p.header-name,
  p.header-subname {
    display: flex;
    align-items: center;
    margin: 0;
    color: var(--text-color-primary);

    a {
      text-decoration: none;
      line-height: 1;
    }

    a:hover {
      color: var(--hover-text-color);
      text-decoration: underline;
    }
  }

  p.header-name {
    min-height: 20px;
    img {
      height: 12px;
      width: 12px;
    }
    a {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-color-primary);
    }
    .MuiSvgIcon-root {
      height: 20px;
      width: 20px;
      margin: 0 2px;
    }
  }

  p.header-subname {
    a {
      font-size: 12px;
      font-weight: 600;
      color: var(--text-color-secondary);
    }
  }
`;

const Body = styled.div`
  padding: 8px 12px 8px 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2px 12px 4px 48px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const Count = styled.span``;

const IconButtonWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-right: 8px;
  &:last-child {
    margin-right: 0;
  }
  .MuiIconButton-root {
    padding: 5px;
  }
  svg {
    height: 14px;
    width: 14px;
  }
`;

const WriteYourComment = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  .MuiAvatar-root {
    width: 36px;
    height: 36px;
  }
  .MuiButton-outlined {
    height: 36px;
    padding: 3px 3px 3px 10px;
    margin-left: 8px;
    border-radius: 25px;
    flex: 1;
    justify-content: flex-start;
    font-size: 14px;
    font-weight: 400;
    color: var(--text-color-secondary);
    background-color: #ffffff;
    text-transform: initial;
  }
`;
